import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";

import {
  ChevronDown,
  Menu,
  X,
  Globe,
   LayoutDashboard,
  Wrench,
  Search,
  Share2,
  Settings,
  FileText,
  HelpCircle,
  ArrowRight,
} from "lucide-react";



const navItems = [
  { name: "Home", path: "/" },
 {
  name: "Services",
  path: "#",
  dropdown: [
    // { name: "Services Overview", path: "/solutions", icon: LayoutDashboard },
    { name: "Website Development", path: "/services/website-development", icon: Globe },
    { name: "Custom Tool Creation", path: "/services/custom-tool-creation", icon: Wrench },
    { name: "SEO Services", path: "/services/seo-services", icon: Search },
    { name: "Social Media Marketing", path: "/services/social-media-marketing", icon: Share2 },
    { name: "Maintenance & Support", path: "/services/maintenance-support", icon: Settings },
  ],
},
  {
    name: "About Us",
    path: "/company/about",
  },
  { name: "Contact", path: "/contact" },
];

const megaMenus = {
  Resources: {
    items: [
      {
        name: "FAQs",
        path: "/resources/faqs",
        icon: HelpCircle,
        description: "Common questions answered",
      },
      {
        name: "Blog",
        path: "/blog",
        icon: FileText,
        description: "Insights & articles",
      },
    ],
  },
};

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const mobilePanelRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileOpenSection(null);
    setActiveDropdown(null);
    setActiveMegaMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);

    setTimeout(() => {
      mobilePanelRef.current?.focus?.();
    }, 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileMenuOpen]);

  const isActivePath = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const toggleMobileSection = (name) => {
    setMobileOpenSection((prev) => (prev === name ? null : name));
  };

  const handleMobileNavigate = (path) => {
    setMobileMenuOpen(false);
    setMobileOpenSection(null);
    navigate(path);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[999] transition-all duration-300 bg-white ${
          isScrolled
            ? "shadow-[0_10px_30px_rgba(15,23,42,0.08)]"
            : "shadow-[0_4px_16px_rgba(15,23,42,0.04)]"
        }`}
      >
        <nav className="w-full">
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 xl:px-10">
            <div className="flex items-center justify-between h-[84px] lg:h-[92px]">
              <div className="flex items-center shrink-0 pr-3 lg:pr-6 min-w-0">
                <Link to="/" className="flex items-center shrink-0">
<motion.img
  src="/logo3.webp"
  alt="Square Trigger"
  className="h-[42px] w-auto max-w-[160px] sm:h-14 sm:max-w-[200px] lg:h-16 lg:max-w-none xl:h-[68px] object-contain shrink-0"
  whileHover={{ scale: 1.02 }}
  transition={{ duration: 0.2 }}
/>
                </Link>
              </div>

              <div className="hidden lg:flex flex-1 items-center justify-center min-w-0">
                <div className="flex items-center justify-center gap-1 xl:gap-2">
                  {navItems.map((item) => (
                    <div
                      key={item.name}
                      className="relative"
                      onMouseEnter={() => {
                        setActiveMegaMenu(null);
                        if (item.dropdown) setActiveDropdown(item.name);
                      }}
                      onMouseLeave={() => {
                        if (item.dropdown) setActiveDropdown(null);
                      }}
                    >
                     {item.dropdown ? (
  <button
    type="button"
    className={`h-12 px-4 xl:px-5 rounded-xl text-[15px] font-medium transition-all duration-200 inline-flex items-center justify-center gap-1.5 whitespace-nowrap ${
      activeDropdown === item.name
        ? "text-primary bg-accent/45"
        : "text-foreground/85 hover:text-foreground hover:bg-accent/45"
    }`}
  >
    {item.name}
    <ChevronDown
      className={`h-4 w-4 transition-transform duration-200 ${
        activeDropdown === item.name ? "rotate-180" : ""
      }`}
    />
  </button>
) : (
  <Link
    to={item.path}
    className={`h-12 px-4 xl:px-5 rounded-xl text-[15px] font-medium transition-all duration-200 inline-flex items-center justify-center gap-1.5 whitespace-nowrap ${
      isActivePath(item.path)
        ? "text-primary bg-accent/45"
        : "text-foreground/85 hover:text-foreground hover:bg-accent/45"
    }`}
  >
    {item.name}
  </Link>
)}
                      <AnimatePresence>
                        {item.dropdown && activeDropdown === item.name && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.18 }}
                            className="absolute top-full left-0 mt-3 w-72 bg-white border border-slate-200 rounded-2xl shadow-xl p-2 z-[1001]"
                          >
                            {item.dropdown.map((subItem) => {
                              const Icon = subItem.icon;
                              return (
                                <Link
                                  key={subItem.name}
                                  to={subItem.path}
                                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 hover:bg-accent group"
                                >
                                  <div className="p-2 rounded-xl bg-accent group-hover:bg-primary/10 transition-colors">
                                    <Icon className="h-4 w-4 text-primary" />
                                  </div>
                                  <span className="font-medium text-foreground">{subItem.name}</span>
                                </Link>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}

                  {Object.keys(megaMenus).map((menuName) => (
                    <div
                      key={menuName}
                      className="relative"
                      onMouseEnter={() => {
                        setActiveDropdown(null);
                        setActiveMegaMenu(menuName);
                      }}
                      onMouseLeave={() => setActiveMegaMenu(null)}
                    >
                      <button
                        type="button"
                        className={`h-12 px-4 xl:px-5 rounded-xl text-[15px] font-medium transition-all duration-200 inline-flex items-center justify-center gap-1.5 whitespace-nowrap ${
                          activeMegaMenu === menuName
                            ? "text-foreground bg-accent/45"
                            : "text-foreground/85 hover:text-foreground hover:bg-accent/45"
                        }`}
                      >
                        {menuName}
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${
                            activeMegaMenu === menuName ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      <AnimatePresence>
                        {activeMegaMenu === menuName && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.18 }}
                            className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[440px] bg-white border border-slate-200 rounded-2xl shadow-xl p-4 z-[1001]"
                          >
                            <div className="grid grid-cols-1 gap-1">
                              {megaMenus[menuName].items.map((item) => {
                                const Icon = item.icon;
                                return (
                                  <Link
                                    key={item.name}
                                    to={item.path}
                                    className="flex items-start gap-3 px-3 py-3 rounded-xl transition-all duration-200 hover:bg-accent group"
                                  >
                                    <div className="p-2 rounded-xl bg-accent group-hover:bg-primary/10 transition-colors flex-shrink-0">
                                      <Icon className="h-4 w-4 text-primary" />
                                    </div>
                                    <div>
                                      <span className="font-medium text-foreground block">{item.name}</span>
                                      <span className="text-xs text-muted-foreground">
                                        {item.description}
                                      </span>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>

              <div className="hidden lg:flex items-center shrink-0 pl-3 lg:pl-6">
                <div className="flex items-center gap-3">
                  <Button
                    variant="gradient"
                    asChild
                    className="h-12 px-6 rounded-xl group text-[15px] font-semibold whitespace-nowrap"
                  >
                    <Link to="/get-started" className="inline-flex items-center gap-2">
                      Book a Free Consultation
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="lg:hidden flex items-center">
                <button
                  type="button"
                  aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                  aria-expanded={mobileMenuOpen}
                  aria-controls="mobile-menu-panel"
                  className="inline-flex items-center justify-center h-12 w-12 rounded-2xl border border-slate-200 bg-white text-foreground hover:bg-accent transition-colors relative z-[2002]"
                  onClick={() => setMobileMenuOpen((prev) => !prev)}
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0 bg-black/45 z-[1995]"
              onClick={() => setMobileMenuOpen(false)}
            />

            <motion.aside
              id="mobile-menu-panel"
              ref={mobilePanelRef}
              tabIndex={-1}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.28 }}
              className="lg:hidden fixed top-0 right-0 h-[100dvh] w-[88vw] max-w-[390px] bg-white border-l border-slate-200 shadow-2xl z-[2000] flex flex-col pointer-events-auto"
            >
              <div className="flex items-center justify-between min-h-[84px] px-4 border-b border-slate-200 shrink-0">
                <button
                  type="button"
                  onClick={() => handleMobileNavigate("/")}
                  className="flex items-center shrink-0"
                >
                  <img
  src="/logo3.webp"
  alt="Square Trigger"
  className="h-[44px] w-auto max-w-[170px] sm:max-w-[190px] object-contain shrink-0"
/>
                </button>

                <button
                  type="button"
                  aria-label="Close menu"
                  className="inline-flex items-center justify-center h-11 w-11 rounded-2xl border border-slate-200 bg-white text-foreground hover:bg-accent transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-3 py-4">
                <div className="space-y-2">
                  {navItems.map((item) => {
                    const hasDropdown = !!item.dropdown;

                    if (!hasDropdown) {
                      return (
                        <button
                          key={item.name}
                          type="button"
                          onClick={() => handleMobileNavigate(item.path)}
                          className={`w-full flex items-center justify-between min-h-[54px] px-4 rounded-2xl transition-colors font-medium text-[15px] ${
                            isActivePath(item.path)
                              ? "bg-accent text-foreground"
                              : "text-foreground hover:bg-accent"
                          }`}
                        >
                          <span>{item.name}</span>
                          <ArrowRight className="h-4 w-4 opacity-60" />
                        </button>
                      );
                    }

                    return (
                      <div
                        key={item.name}
                        className="rounded-2xl border border-slate-200 overflow-hidden bg-white"
                      >
                        <div className="flex items-center">
                          <button
  type="button"
  onClick={() => {
    if (item.dropdown) {
      toggleMobileSection(item.name);
      return;
    }
    handleMobileNavigate(item.path);
  }}
  className={`flex-1 min-h-[54px] px-4 flex items-center text-left font-medium text-[15px] transition-colors ${
    isActivePath(item.path)
      ? "bg-accent text-foreground"
      : "text-foreground hover:bg-accent"
  }`}
>
  <span>{item.name}</span>
</button>

                          <button
                            type="button"
                            aria-label={`Toggle ${item.name} menu`}
                            onClick={() => toggleMobileSection(item.name)}
                            className="h-[54px] w-[54px] flex items-center justify-center border-l border-slate-200 hover:bg-accent transition-colors"
                          >
                            <ChevronDown
                              className={`h-5 w-5 transition-transform ${
                                mobileOpenSection === item.name ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                        </div>

                        <AnimatePresence initial={false}>
                          {mobileOpenSection === item.name && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="border-t border-slate-200 bg-background/40 overflow-hidden"
                            >
                              <div className="p-2 space-y-1">
                                

                                {item.dropdown.map((subItem) => {
                                  const Icon = subItem.icon;
                                  return (
                                    <button
                                      key={subItem.name}
                                      type="button"
                                      onClick={() => handleMobileNavigate(subItem.path)}
                                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-colors ${
                                        isActivePath(subItem.path)
                                          ? "bg-accent text-foreground"
                                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                                      }`}
                                    >
                                      <span className="p-2 rounded-xl bg-accent/70 shrink-0">
                                        <Icon className="h-4 w-4 text-primary" />
                                      </span>
                                      <span className="font-medium">{subItem.name}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 space-y-2">
                  {Object.entries(megaMenus).map(([menuName, menu]) => (
                    <div
                      key={menuName}
                      className="rounded-2xl border border-slate-200 overflow-hidden bg-white"
                    >
                      <button
                        type="button"
                        onClick={() => toggleMobileSection(menuName)}
                        className="w-full flex items-center justify-between min-h-[54px] px-4 text-left font-medium text-[15px] hover:bg-accent transition-colors"
                      >
                        <span className="text-foreground">{menuName}</span>
                        <ChevronDown
                          className={`h-5 w-5 transition-transform ${
                            mobileOpenSection === menuName ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      <AnimatePresence initial={false}>
                        {mobileOpenSection === menuName && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="border-t border-slate-200 bg-background/40 overflow-hidden"
                          >
                            <div className="p-2 space-y-1">
                              {menu.items.map((item) => {
                                const Icon = item.icon;
                                return (
                                  <button
                                    key={item.name}
                                    type="button"
                                    onClick={() => handleMobileNavigate(item.path)}
                                    className={`w-full flex items-start gap-3 px-3 py-3 rounded-xl text-sm transition-colors ${
                                      isActivePath(item.path)
                                        ? "bg-accent text-foreground"
                                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                                    }`}
                                  >
                                    <span className="p-2 rounded-xl bg-accent/70 mt-0.5 shrink-0">
                                      <Icon className="h-4 w-4 text-primary" />
                                    </span>
                                    <span className="leading-tight text-left">
                                      <span className="font-medium block text-foreground">
                                        {item.name}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        {item.description}
                                      </span>
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>

                <div className="mt-5 pt-4 border-t border-slate-200 space-y-3 pb-6">
                  <button
                    type="button"
                    onClick={() => handleMobileNavigate("/get-started")}
                    className="w-full h-12 rounded-xl text-[15px] font-semibold text-white bg-gradient-to-r from-purple-600 to-sky-500 hover:opacity-95 transition-opacity"
                  >
                    Book a Free Consultation
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};